import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  ArrowRight,
  CheckCircle,
  Compass,
  Handshake,
  ChartLineUp,
  UsersThree,
  Database,
} from "@phosphor-icons/react";

const COPY = {
  cs: {
    eyebrow: "Pilíř 05 / Retail Consulting",
    title: "Víme, jak retail funguje zevnitř.",
    sub:
      "Divize Retail Consulting vznikla s přesvědčením, že dobrá retailová strategie nestojí na obecných doporučeních — stojí na konkrétní znalosti prostředí, vztazích v trhu a praxi, kterou nelze nasimulovat. Pomáháme výrobcům a dodavatelům zorientovat se v prostředí moderního retailu, vyjednávat s obchodními řetězci z pozice síly a stavět obchodní strategie, které skutečně fungují na prodejní ploše.",
    leadIn: "S více než 28 lety retailové praxe za zády. Z terénu, ne z učebnic.",
    overview: {
      eyebrow: "Přehled našich služeb",
      title: "Pět oblastí, ve kterých výrobci nejčastěji ztrácejí peníze.",
      sub:
        "Pracujeme s vámi jako partner — ne jako konzultant s prezentací, ale jako zkušený spoluhráč u jednacího stolu.",
    },
    services: [
      {
        n: "01",
        anchor: "go-to-market",
        icon: "Compass",
        tag: "Go-to-Market",
        title: "Nový produkt nestačí postavit na regál. Musí tam obstát.",
        lead:
          "Uvést nový produkt do retailu je jedna z nejnáročnějších obchodních výzev. Nestačí mít dobrý produkt — potřebujete správný kanál, timing, cenu a přesvědčivý příběh pro retailera i pro zákazníky. Všechno najednou. V Global Marketing Retail Consulting vám pomůžeme celý tento proces naplánovat a řídit — od prvního strategického rozhodnutí až po den, kdy váš produkt poprvé zamíří z police do košíku zákazníka.",
        blocks: [
          {
            h: "Mapování kategorie a kanálů",
            t: "Než vstoupíte do kategorie, musíte ji důkladně znát. Zmapujeme konkurenční prostředí, cenové hladiny, chování zákazníků i bílá místa na trhu, která váš produkt může obsadit. Ne každý produkt patří do každého řetězce — pomůžeme vám rozhodnout, kde začít s distribucí.",
          },
          {
            h: "Argumentace a launch",
            t: "Připravíme přesvědčivou obchodní argumentaci postavenou na datech, nastavíme cenový positioning a plán úvodních promocí. Zkoordinujeme samotný vstup produktu na trh — od dodávek a merchandisingu přes POS materiály až po sledování výsledků v prvních týdnech po launchi.",
          },
          {
            h: "Sledování po launchi",
            t: "Go-to-market není událost. Je to proces. Úspěšný launch se měří až po prvních třech měsících na trhu. Naše podpora nekončí dnem uvedení produktu — sledujeme výsledky, vyhodnocujeme data a v případě potřeby strategii operativně upravujeme.",
          },
        ],
        callout:
          "Vstup nového produktu do retailu je drahý pokus, pokud se nepovede. Špatně zvolený kanál, podcenění vyjednávání s řetězcem nebo slabá in-store prezentace mohou rozhodnout o tom, že produkt skončí de-listingem dřív, než dostane šanci.",
      },
      {
        n: "02",
        anchor: "vyjednavani",
        icon: "Handshake",
        tag: "Vyjednávání",
        title: "Vyjednávání s řetězci je disciplína, ne náhoda.",
        lead:
          "Každý výrobce, který chce prodávat v moderním retailu, dříve nebo později sedí naproti nákupčímu velkého řetězce. Tento moment rozhoduje o tom, za jakých podmínek — a zda vůbec — se jeho produkt dostane na polici. Špatně připravené vyjednávání stojí peníze, marži i pozici v kategorii. Dobře připravené naopak otevírá dlouhodobé partnerství.",
        blocks: [
          {
            h: "Zalistování produktu",
            t: "Zalistování není jen administrativní úkon — je to obchodní rozhodnutí, které řetězec posuzuje velmi přísně. Pomůžeme vám pochopit, jak celý proces funguje zevnitř, jaká jsou kritéria hodnocení a jak se vyhnout nejčastějším chybám, které zalistování zbrzdí nebo úplně zastaví.",
          },
          {
            h: "Příprava na obchodní jednání",
            t: "Obchodní podmínky s řetězci jsou komplexní systém vzájemných závazků — výrobci, kteří je přijímají bez přípravy, platí víc, než je nutné. Připravíme vás na jednání od analýzy aktuálních podmínek přes simulaci scénářů až po konkrétní argumenty, které obstojí i u zkušeného nákupčího.",
          },
          {
            h: "In-store standardy a kontrola",
            t: "Pomůžeme vám nastavit standardy in-store prezentace, připravit trade marketingovou podporu pro klíčové zákazníky a průběžně ověřovat, zda jsou ze strany retailera dodržovány nastavené obchodní podmínky.",
          },
          {
            h: "Reputace a vztahy",
            t: "V retailu se ví všechno. Vaše reputace vás předchází dřív, než vstoupíte do zasedací místnosti. Dlouhodobý úspěch v retailu nestojí jen na podmínkách — stojí na důvěře. Pomůžeme vám budovat vztahy s klíčovými zákazníky systematicky, od správné komunikace přes zvládání krizových situací až po ochranu vaší reputace.",
          },
        ],
      },
      {
        n: "03",
        anchor: "jbp",
        icon: "ChartLineUp",
        tag: "Joint Business Plan",
        title: "Nejlepší vyjednávání není o tom, kdo víc ustoupí. Je o tom, kde je průsečík zájmů.",
        lead:
          "Joint Business Plan je nástroj, který přesouvá vztah výrobce a retailera z každoročního přetahování o podmínky na společné plánování růstu. Dobře postavený JBP definuje společné cíle, rozděluje investice a jasně říká, co každá strana vkládá — a co za to dostává. Společný obchodní plán je dnes standardem ve vyjednávání s klíčovými retailery.",
        blocks: [
          {
            h: "Strukturujeme od základu",
            t: "Pomůžeme vám JBP připravit od základu: identifikujeme příležitosti pro obě strany, nastavíme obchodní cíle, strukturujeme investice do zákazníka a navrhneme adekvátní protiplnění. Výsledkem není jen dokument — je to základ pro partnerství, které má smysl pro vás i pro řetězec.",
          },
          {
            h: "Argumentace daty",
            t: "Úspěch v retailu se neměří jen vaším obratem, ale i tím, jak vypadá celá kategorie. Pomůžeme vám pochopit nákupní chování zákazníků, zmonitorovat planogram a argumentovat retailerovi daty, ne pocity. Naše vlastní auditní systémy vám dají přesná data o dostupnosti, umístění a prezentaci.",
          },
        ],
        callout:
          "Chcete vědět, jestli váš JBP obstojí? Nabízíme nezávazný úvodní audit vaší obchodní strategie. Řekneme vám na rovinu, kde máte rezervy a jak je využít.",
      },
      {
        n: "04",
        anchor: "lidske-zdroje",
        icon: "UsersThree",
        tag: "Lidské zdroje",
        title: "Obchodní výsledky dělají lidé. A lidé potřebují správné nastavení.",
        lead:
          "Sebelepší strategie zůstane na papíře, pokud ji nemá kdo realizovat. Složení obchodního týmu, jeho kompetence, motivační systém a schopnost vyjednávat — to jsou proměnné, které rozhodují o tom, zda vaše obchodní cíle zůstanou v prezentaci, nebo se promítnou do výsledků.",
        blocks: [
          {
            h: "Interní vs. externí tým",
            t: "Interní, externí, nebo kombinace? Neexistuje univerzální odpověď — záleží na velikosti firmy, fázi růstu, počtu klíčových zákazníků a finančních možnostech. Pomůžeme vám najít model, který dává smysl právě pro vás.",
          },
          {
            h: "KPI a motivace",
            t: "Špatně nastavené KPI demotivují — nebo motivují ke špatným věcem. Pomůžeme vám nastavit měřitelné cíle, které jsou provázané s obchodní strategií, a motivační systém, který obchodníky skutečně táhne správným směrem.",
          },
          {
            h: "Kompetence Key Account Managerů",
            t: "Key account manažer je tváří vaší firmy u nejdůležitějších zákazníků. Musí rozumět retailu, umět číst data, připravit JBP a obstát u zkušeného nákupčího. Pomůžeme vám definovat, co od KAM skutečně potřebujete, nastavit kompetenční model a identifikovat mezery, které je potřeba překlenout.",
          },
          {
            h: "Tréninky vyjednávání",
            t: "Vyjednávání se dá naučit — ale ne z knih. Nabízíme tréninky vyjednávacích dovedností přímo pro obchodní týmy výrobců a dodavatelů, zaměřené na prostředí retailu, konkrétní typy jednání s řetězci a situace, které vaši obchodníci skutečně zažívají. Žádné obecné techniky — jen to, co funguje u nákupčích předních retailerů.",
          },
        ],
      },
      {
        n: "05",
        anchor: "instore-data",
        icon: "Database",
        tag: "Instore Data",
        title: "Co nevidíte na prodejním místě, nemůžete řídit.",
        lead:
          "Víte, za kolik se váš produkt skutečně prodává? Zda stojí na správném místě police? Zda vůbec stojí na polici? A co dělá konkurence ve stejné chvíli ve stejné prodejně? Instore data jsou odpovědi na tyto otázky — v reálném čase, z každé prodejny, přehledně. Global Marketing provozuje vlastní systémy pro sběr, zpracování a reporting instore dat. Pracujeme s fakty z terénu — ne s odhady. A sledujeme nejen vaše produkty, ale také konkurenci.",
        blocks: [
          {
            h: "Price monitoring",
            t: "Systematické sledování skutečných cen vašich produktů i konkurenčních značek v celé síti. Cenové odchylky oproti RRP, akční a promoční ceny, dlouhodobé trendy. Pokud se váš produkt pravidelně nachází pod doporučenou cenou v klíčových řetězcích, poškozuje to váš brand positioning.",
          },
          {
            h: "Shelf monitoring",
            t: "Systematické ověřování toho, jak váš produkt skutečně vypadá v místě prodeje — umístění, planogram, dostupnost, cenový štítek. Pro každý EAN zaznamenáváme kompletní datovou sadu: identifikaci produktu, cenu, prezentaci na polici a parametry umístění.",
          },
          {
            h: "Druhotná umístění",
            t: "Ověřujeme přítomnost a stav všech sjednaných druhotných umístění a zároveň mapujeme celou kategorii — vlastní i konkurenční. Výsledkem je kompletní kategoriální pohled, který samotný monitoring vlastních umístění nikdy neposkytne.",
          },
          {
            h: "POS monitoring",
            t: "Sleduje přítomnost, stav a soulad vašich POS materiálů s aktuální kampaní — a zároveň mapuje POS komunikaci konkurence. Cenný vstup pro trade marketingové plánování a vyjednávání s řetězci o exkluzivitě komunikačních zón.",
          },
        ],
        callout:
          "Všechna data sbíráme přes MERVIK, zpracováváme v SYMES a vizualizujeme v QlikView. Žádné složité tabulky v Excelu, žádné čekání na report — data jsou k dispozici okamžitě, připravená k prezentaci pro váš obchodní tým i pro jednání s retailerem.",
        cta: { label: "Detail Instore Data", to: "/instore-data" },
      },
    ],
    finalCta: {
      eyebrow: "Připraveni zahájit spolupráci?",
      title: "Řekneme vám na rovinu, kde máte rezervy a jak je využít.",
      sub: "První konzultace je zdarma — bez závazků, s konkrétním výstupem.",
      email: "info@globalmarketing.cz",
      web: "www.globalmarketing.cz",
    },
  },
  en: {
    eyebrow: "Pillar 05 / Retail Consulting",
    title: "We know how retail works from the inside.",
    sub:
      "The Retail Consulting division was built on the conviction that good retail strategy doesn't rest on generic recommendations — it rests on concrete knowledge of the environment, market relationships and practice you cannot simulate. We help manufacturers and suppliers navigate modern retail, negotiate with chains from a position of strength and build commercial strategies that actually work on the shop floor.",
    leadIn: "Backed by 28+ years of retail practice. From the field, not the textbook.",
    overview: {
      eyebrow: "Service overview",
      title: "Five areas where manufacturers most often lose money.",
      sub:
        "We work with you as a partner — not a consultant with a deck, but an experienced player at the negotiation table.",
    },
    services: [
      {
        n: "01",
        anchor: "go-to-market",
        icon: "Compass",
        tag: "Go-to-Market",
        title: "Putting a new product on the shelf isn't enough. It has to hold there.",
        lead:
          "Launching a new product into retail is one of the toughest commercial challenges. A good product alone isn't enough — you need the right channel, timing, price and a convincing story for the retailer and for the shopper. All at once. We help you plan and run the entire process — from the first strategic decision to the day your product first moves from shelf into a basket.",
        blocks: [
          { h: "Category and channel mapping", t: "Before entering a category, you must know it deeply. We map the competitive landscape, price tiers, shopper behaviour and white space your product can occupy. Not every product belongs in every chain — we help you decide where to start." },
          { h: "Argumentation and launch", t: "We build a data-led commercial argument, set price positioning and the launch promo plan. We coordinate the actual market entry — supply, merchandising, POS, and tracking the first weeks after launch." },
          { h: "Post-launch tracking", t: "Go-to-market isn't an event. It's a process. A successful launch is measured after the first three months. Our support doesn't end on launch day — we monitor results, evaluate data and adjust the strategy operationally." },
        ],
        callout:
          "Entering retail is an expensive experiment when it fails. The wrong channel, weak chain negotiation or poor in-store presentation can de-list a product before it gets a chance.",
      },
      {
        n: "02",
        anchor: "vyjednavani",
        icon: "Handshake",
        tag: "Negotiation",
        title: "Negotiating with chains is a discipline, not luck.",
        lead:
          "Every manufacturer who wants to sell in modern retail eventually sits across a buyer at a major chain. That moment decides on what terms — and whether at all — the product reaches the shelf. A poorly prepared negotiation costs money, margin and category position. A well-prepared one opens long-term partnership.",
        blocks: [
          { h: "Listing", t: "Listing isn't paperwork — it's a commercial decision the chain assesses strictly. We help you understand how the process works inside, what the assessment criteria are and how to avoid the most common mistakes that stall or kill a listing." },
          { h: "Negotiation prep", t: "Trade terms with chains are a complex web of commitments — manufacturers who accept them unprepared overpay. We prepare you from analysing current terms through scenario simulation to specific arguments that hold up against an experienced buyer." },
          { h: "In-store standards and control", t: "We help set in-store presentation standards, prepare trade marketing support for key customers and continuously verify whether the retailer is honouring agreed terms." },
          { h: "Reputation and relationships", t: "In retail, everyone knows. Your reputation precedes you. Long-term success rests on trust. We help you build relationships with key customers systematically, from communication through crisis handling to reputation protection." },
        ],
      },
      {
        n: "03",
        anchor: "jbp",
        icon: "ChartLineUp",
        tag: "Joint Business Plan",
        title: "The best negotiation isn't about who concedes more. It's about where interests overlap.",
        lead:
          "A Joint Business Plan moves the manufacturer–retailer relationship from yearly haggling over terms to joint growth planning. A well-built JBP defines shared goals, splits investments and clearly says what each side puts in — and what they get back. JBPs are now the standard with key retailers.",
        blocks: [
          { h: "Built from the ground up", t: "We help you build a JBP from the ground: identify opportunities for both sides, set commercial goals, structure customer investments and design fair counter-value. The result isn't just a document — it's the foundation for a partnership that makes sense for both sides." },
          { h: "Data-driven arguments", t: "Retail success isn't measured only by your turnover, but by what the whole category looks like. We help you understand shopper behaviour, audit planograms and argue with data, not feelings." },
        ],
        callout:
          "Want to know if your JBP will hold? We offer a non-binding strategy audit. We'll tell you straight where you have headroom and how to use it.",
      },
      {
        n: "04",
        anchor: "lidske-zdroje",
        icon: "UsersThree",
        tag: "People & org",
        title: "Results are made by people. And people need the right setup.",
        lead:
          "Even the best strategy stays on paper if there's no one to execute it. The composition of the sales team, its competencies, incentive system and ability to negotiate — these are the variables that decide whether your goals stay in the deck or land in the numbers.",
        blocks: [
          { h: "Internal vs. external", t: "In-house, external, or hybrid? There's no universal answer — it depends on size, growth stage, number of key customers and budget. We'll help you find the right model." },
          { h: "KPIs and incentives", t: "Wrong KPIs demotivate — or motivate the wrong things. We'll set measurable goals tied to commercial strategy and an incentive system that pulls reps in the right direction." },
          { h: "KAM competencies", t: "A KAM is the face of your company at the most important customers. They must understand retail, read data, build a JBP and hold up against an experienced buyer. We help define the competency model and identify gaps." },
          { h: "Negotiation training", t: "Negotiation can be learned — but not from books. We deliver negotiation training tailored to retail, real chain situations and the kind of conversations your reps actually face. No generic techniques — only what works with the buyers of leading retailers." },
        ],
      },
      {
        n: "05",
        anchor: "instore-data",
        icon: "Database",
        tag: "Instore Data",
        title: "What you don't see at the point of sale, you can't manage.",
        lead:
          "Do you know what your product actually sells for? Whether it sits in the right shelf position? Whether it sits at all? And what your competitor is doing in that same store at the same moment? Instore data answers these questions — in real time, from every store. We work with field facts — not estimates. We monitor your products and the competition.",
        blocks: [
          { h: "Price monitoring", t: "Systematic tracking of real prices for your products and competing brands across the network. Price deviations vs. RRP, promo prices, long-term trends. If your product routinely sits below RRP in key chains, it damages brand positioning." },
          { h: "Shelf monitoring", t: "Systematic verification of how your product looks at point of sale — placement, planogram, availability, price tag. For every EAN we capture the full data set: product identity, price, shelf presence and placement parameters." },
          { h: "Secondary placements", t: "We verify the presence and condition of every agreed secondary placement and map the entire category — own and competitor. The result: a category-wide view that own-only monitoring can never deliver." },
          { h: "POS monitoring", t: "Tracks the presence, condition and alignment of your POS with the active campaign — and maps competitor POS in your category. Valuable input for trade marketing and chain negotiations on communication zones." },
        ],
        callout:
          "All data is captured via MERVIK, processed in SYMES and visualised in QlikView. No tangled Excel sheets, no waiting on reports — data ready instantly, presentation-ready for your sales team and retailer meetings.",
        cta: { label: "InStore Data details", to: "/instore-data" },
      },
    ],
    finalCta: {
      eyebrow: "Ready to start?",
      title: "We'll tell you straight where you have headroom and how to use it.",
      sub: "First consultation is free — no commitments, with concrete output.",
      email: "info@globalmarketing.cz",
      web: "www.globalmarketing.cz",
    },
  },
  sk: {
    eyebrow: "Pilier 05 / Retail Consulting",
    title: "Vieme, ako retail funguje zvnútra.",
    sub:
      "Divízia Retail Consulting vznikla s presvedčením, že dobrá retailová stratégia nestojí na všeobecných odporúčaniach — stojí na konkrétnej znalosti prostredia, vzťahoch v trhu a praxi, ktorú nemožno nasimulovať. Pomáhame výrobcom a dodávateľom zorientovať sa v prostredí moderného retailu, vyjednávať s obchodnými reťazcami z pozície sily a stavať obchodné stratégie, ktoré skutočne fungujú na predajnej ploche.",
    leadIn: "S viac ako 28 rokmi retailovej praxe za sebou. Z terénu, nie z učebníc.",
    overview: {
      eyebrow: "Prehľad našich služieb",
      title: "Päť oblastí, v ktorých výrobcovia najčastejšie strácajú peniaze.",
      sub:
        "Pracujeme s vami ako partner — nie ako konzultant s prezentáciou, ale ako skúsený spoluhráč pri rokovacom stole.",
    },
    services: [
      {
        n: "01",
        anchor: "go-to-market",
        icon: "Compass",
        tag: "Go-to-Market",
        title: "Nový produkt nestačí postaviť na regál. Musí tam obstáť.",
        lead:
          "Uviesť nový produkt do retailu je jedna z najnáročnejších obchodných výziev. Nestačí mať dobrý produkt — potrebujete správny kanál, timing, cenu a presvedčivý príbeh pre retailera aj pre zákazníkov. Všetko naraz. V Global Marketing Retail Consulting vám pomôžeme celý tento proces naplánovať a riadiť — od prvého strategického rozhodnutia až po deň, keď váš produkt prvýkrát zamieri z police do košíka zákazníka.",
        blocks: [
          {
            h: "Mapovanie kategórie a kanálov",
            t: "Skôr než vstúpite do kategórie, musíte ju dôkladne poznať. Zmapujeme konkurenčné prostredie, cenové hladiny, správanie zákazníkov aj biele miesta na trhu, ktoré váš produkt môže obsadiť. Nie každý produkt patrí do každého reťazca — pomôžeme vám rozhodnúť, kde začať s distribúciou.",
          },
          {
            h: "Argumentácia a launch",
            t: "Pripravíme presvedčivú obchodnú argumentáciu postavenú na dátach, nastavíme cenový positioning a plán úvodných promocií. Skoordinujeme samotný vstup produktu na trh — od dodávok a merchandisingu cez POS materiály až po sledovanie výsledkov v prvých týždňoch po launchi.",
          },
          {
            h: "Sledovanie po launchi",
            t: "Go-to-market nie je udalosť. Je to proces. Úspešný launch sa meria až po prvých troch mesiacoch na trhu. Naša podpora nekončí dňom uvedenia produktu — sledujeme výsledky, vyhodnocujeme dáta a v prípade potreby stratégiu operatívne upravujeme.",
          },
        ],
        callout:
          "Vstup nového produktu do retailu je drahý pokus, pokiaľ sa nepodarí. Zle zvolený kanál, podcenenie vyjednávania s reťazcom alebo slabá in-store prezentácia môžu rozhodnúť o tom, že produkt skončí de-listingom skôr, než dostane šancu.",
      },
      {
        n: "02",
        anchor: "vyjednavanie",
        icon: "Handshake",
        tag: "Vyjednávanie",
        title: "Vyjednávanie s reťazcami je disciplína, nie náhoda.",
        lead:
          "Každý výrobca, ktorý chce predávať v modernom retaile, skôr či neskôr sedí oproti nákupcovi veľkého reťazca. Tento moment rozhoduje o tom, za akých podmienok — a či vôbec — sa jeho produkt dostane na policu. Zle pripravené vyjednávanie stojí peniaze, maržu aj pozíciu v kategórii. Dobre pripravené naopak otvára dlhodobé partnerstvo.",
        blocks: [
          {
            h: "Zalistovanie produktu",
            t: "Zalistovanie nie je len administratívny úkon — je to obchodné rozhodnutie, ktoré reťazec posudzuje veľmi prísne. Pomôžeme vám pochopiť, ako celý proces funguje zvnútra, aké sú kritériá hodnotenia a ako sa vyhnúť najčastejším chybám, ktoré zalistovanie zbrzdia alebo úplne zastavia.",
          },
          {
            h: "Príprava na obchodné jednanie",
            t: "Obchodné podmienky s reťazcami sú komplexný systém vzájomných záväzkov — výrobcovia, ktorí ich prijímajú bez prípravy, platia viac, než je potrebné. Pripravíme vás na jednanie od analýzy aktuálnych podmienok cez simuláciu scenárov až po konkrétne argumenty, ktoré obstoja aj u skúseného nákupcu.",
          },
          {
            h: "In-store štandardy a kontrola",
            t: "Pomôžeme vám nastaviť štandardy in-store prezentácie, pripraviť trade marketingovú podporu pre kľúčových zákazníkov a priebežne overovať, či sú zo strany retailera dodržiavané nastavené obchodné podmienky.",
          },
          {
            h: "Reputácia a vzťahy",
            t: "V retaile sa vie všetko. Vaša reputácia vás predchádza skôr, než vstúpite do zasadacej miestnosti. Dlhodobý úspech v retaile nestojí len na podmienkach — stojí na dôvere. Pomôžeme vám budovať vzťahy s kľúčovými zákazníkmi systematicky, od správnej komunikácie cez zvládanie krízových situácií až po ochranu vašej reputácie.",
          },
        ],
      },
      {
        n: "03",
        anchor: "jbp",
        icon: "ChartLineUp",
        tag: "Joint Business Plan",
        title: "Najlepšie vyjednávanie nie je o tom, kto viac ustúpi. Je o tom, kde je prienik záujmov.",
        lead:
          "Joint Business Plan je nástroj, ktorý presúva vzťah výrobcu a retailera z každoročného preťahovania o podmienky na spoločné plánovanie rastu. Dobre postavený JBP definuje spoločné ciele, rozdeľuje investície a jasne hovorí, čo každá strana vkladá — a čo za to dostáva. Spoločný obchodný plán je dnes štandardom vo vyjednávaní s kľúčovými retailermi.",
        blocks: [
          {
            h: "Štruktúrujeme od základu",
            t: "Pomôžeme vám JBP pripraviť od základu: identifikujeme príležitosti pre obe strany, nastavíme obchodné ciele, štruktúrujeme investície do zákazníka a navrhneme adekvátne protiplnenie. Výsledkom nie je len dokument — je to základ pre partnerstvo, ktoré má zmysel pre vás aj pre reťazec.",
          },
          {
            h: "Argumentácia dátami",
            t: "Úspech v retaile sa nemeria len vaším obratom, ale aj tým, ako vyzerá celá kategória. Pomôžeme vám pochopiť nákupné správanie zákazníkov, zmonitorovať plánogram a argumentovať retailerovi dátami, nie pocitmi. Naše vlastné auditné systémy vám dajú presné dáta o dostupnosti, umiestnení a prezentácii.",
          },
        ],
        callout:
          "Chcete vedieť, či váš JBP obstojí? Ponúkame nezáväzný úvodný audit vašej obchodnej stratégie. Povieme vám na rovinu, kde máte rezervy a ako ich využiť.",
      },
      {
        n: "04",
        anchor: "ludske-zdroje",
        icon: "UsersThree",
        tag: "Ľudské zdroje",
        title: "Obchodné výsledky robia ľudia. A ľudia potrebujú správne nastavenie.",
        lead:
          "Akokoľvek dobrá stratégia zostane na papieri, pokiaľ ju nemá kto realizovať. Zloženie obchodného tímu, jeho kompetencie, motivačný systém a schopnosť vyjednávať — to sú premenné, ktoré rozhodujú o tom, či vaše obchodné ciele zostanú v prezentácii, alebo sa premietnu do výsledkov.",
        blocks: [
          {
            h: "Interný vs. externý tím",
            t: "Interný, externý alebo kombinácia? Neexistuje univerzálna odpoveď — záleží na veľkosti firmy, fáze rastu, počte kľúčových zákazníkov a finančných možnostiach. Pomôžeme vám nájsť model, ktorý dáva zmysel práve pre vás.",
          },
          {
            h: "KPI a motivácia",
            t: "Zle nastavené KPI demotivujú — alebo motivujú k zlým veciam. Pomôžeme vám nastaviť merateľné ciele, ktoré sú prepojené s obchodnou stratégiou, a motivačný systém, ktorý obchodníkov skutočne ťahá správnym smerom.",
          },
          {
            h: "Kompetencie Key Account Managerov",
            t: "Key account manažér je tvárou vašej firmy u najdôležitejších zákazníkov. Musí rozumieť retailu, vedieť čítať dáta, pripraviť JBP a obstáť u skúseného nákupcu. Pomôžeme vám definovať, čo od KAM skutočne potrebujete, nastaviť kompetenčný model a identifikovať medzery, ktoré treba prekonať.",
          },
          {
            h: "Tréningy vyjednávania",
            t: "Vyjednávanie sa dá naučiť — ale nie z kníh. Ponúkame tréningy vyjednávacích zručností priamo pre obchodné tímy výrobcov a dodávateľov, zamerané na prostredie retailu, konkrétne typy jednaní s reťazcami a situácie, ktoré vaši obchodníci skutočne zažívajú.",
          },
        ],
      },
      {
        n: "05",
        anchor: "instore-data",
        icon: "Database",
        tag: "Instore Data",
        title: "Čo nevidíte na predajnom mieste, nemôžete riadiť.",
        lead:
          "Viete, za koľko sa váš produkt skutočne predáva? Či stojí na správnom mieste police? Či vôbec stojí na polici? A čo robí konkurencia v rovnakej chvíli v rovnakej predajni? Instore dáta sú odpovede na tieto otázky — v reálnom čase, z každej predajne, prehľadne. Global Marketing prevádzkuje vlastné systémy na zber, spracovanie a reporting instore dát. Pracujeme s faktami z terénu — nie s odhadmi. A sledujeme nielen vaše produkty, ale aj konkurenciu.",
        blocks: [
          {
            h: "Price monitoring",
            t: "Systematické sledovanie skutočných cien vašich produktov aj konkurenčných značiek v celej sieti. Cenové odchýlky oproti RRP, akciové a promočné ceny, dlhodobé trendy. Pokiaľ sa váš produkt pravidelne nachádza pod odporúčanou cenou v kľúčových reťazcoch, poškodzuje to váš brand positioning.",
          },
          {
            h: "Shelf monitoring",
            t: "Systematické overovanie toho, ako váš produkt skutočne vyzerá na mieste predaja — umiestnenie, plánogram, dostupnosť, cenový štítok. Pre každý EAN zaznamenávame kompletnú dátovú sadu: identifikáciu produktu, cenu, prezentáciu na polici a parametre umiestnenia.",
          },
          {
            h: "Druhotné umiestnenia",
            t: "Overujeme prítomnosť a stav všetkých dohodnutých druhotných umiestnení a zároveň mapujeme celú kategóriu — vlastnú aj konkurenčnú. Výsledkom je kompletný kategoriálny pohľad, ktorý samotný monitoring vlastných umiestnení nikdy neposkytne.",
          },
          {
            h: "POS monitoring",
            t: "Sleduje prítomnosť, stav a súlad vašich POS materiálov s aktuálnou kampaňou — a zároveň mapuje POS komunikáciu konkurencie. Cenný vstup pre trade marketingové plánovanie a vyjednávanie s reťazcami o exkluzivite komunikačných zón.",
          },
        ],
        callout:
          "Všetky dáta zbierame cez MERVIK, spracovávame v SYMES a vizualizujeme v QlikView. Žiadne zložité tabuľky v Exceli, žiadne čakanie na report — dáta sú k dispozícii okamžite, pripravené na prezentáciu pre váš obchodný tím aj pre rokovanie s retailerom.",
        cta: { label: "Detail Instore Data", to: "/instore-data" },
      },
    ],
    finalCta: {
      eyebrow: "Pripravení začať spoluprácu?",
      title: "Povieme vám na rovinu, kde máte rezervy a ako ich využiť.",
      sub: "Prvá konzultácia je zadarmo — bez záväzkov, s konkrétnym výstupom.",
      email: "info@globalmarketing.cz",
      web: "www.globalmarketing.cz",
    },
  },
};

const ICONS = { Compass, Handshake, ChartLineUp, UsersThree, Database };

const Eyebrow = ({ children, accent }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : ""}`}>{children}</span>
);

const Hero = ({ c }) => (
  <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="rc-hero">
    <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9">
          <Eyebrow accent>{c.eyebrow}</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
            {c.title}
          </h1>
          <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{c.sub}</p>
          <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5 italic">
            {c.leadIn}
          </p>
        </div>
        <div className="lg:col-span-3 flex lg:items-end">
          <div className="bg-[#0a0a0a] text-white p-6 w-full">
            <div className="font-mono text-[10px] tracking-widest text-white/50">// EXPERIENCE</div>
            <div className="font-display text-5xl md:text-6xl tracking-tighter mt-3 leading-none">28+</div>
            <div className="text-sm text-white/70 mt-3 leading-relaxed">let retailové praxe v ČR a na Slovensku.</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Overview = ({ c, services }) => (
  <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="rc-overview">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">
            {c.title}
          </h2>
        </div>
        <p className="md:col-span-5 text-[#525252] leading-relaxed">{c.sub}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
        {services.map((s) => {
          const Icon = ICONS[s.icon];
          return (
            <a
              key={s.n}
              href={`#${s.anchor}`}
              className="group bg-white border border-[#e5e5e5] hover:border-[#0a0a0a] transition-colors p-6"
              data-testid={`rc-overview-${s.anchor}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl tracking-tighter text-[#0A4DFF]">/{s.n}</span>
                {Icon ? <Icon size={22} weight="thin" className="text-[#0a0a0a] group-hover:text-[#0A4DFF] transition-colors" /> : null}
              </div>
              <h3 className="font-display text-lg md:text-xl mt-5 leading-tight tracking-tight">{s.tag}</h3>
            </a>
          );
        })}
      </div>
    </div>
  </section>
);

const ServiceSection = ({ s, idx }) => {
  const Icon = ICONS[s.icon];
  const dark = idx % 2 === 1;
  return (
    <section
      id={s.anchor}
      className={`py-14 md:py-20 scroll-mt-20 ${dark ? "bg-[#0a0a0a] text-white" : "bg-white border-y border-[#e5e5e5]"}`}
      data-testid={`rc-service-${s.anchor}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header: number + icon + tag inline */}
        <div className="flex items-center gap-5">
          <span className={`font-display text-6xl md:text-7xl tracking-tighter leading-none ${dark ? "text-[#5B82FF]" : "text-[#0A4DFF]"}`}>/{s.n}</span>
          {Icon ? <Icon size={28} weight="thin" className={dark ? "text-white" : "text-[#0a0a0a]"} /> : null}
          <span className={`font-mono uppercase tracking-[0.22em] text-[12px] ${dark ? "text-white/60" : "text-[#525252]"}`}>{s.tag}</span>
        </div>

        {/* Title — single column, max width */}
        <h2 className="mt-8 font-display text-3xl md:text-5xl tracking-tight leading-[1.05] font-medium max-w-5xl">
          {s.title}
        </h2>

        {/* Lead paragraph — full content width, balanced */}
        <p className={`mt-6 text-base md:text-lg leading-relaxed max-w-4xl ${dark ? "text-white/80" : "text-[#0a0a0a]"}`}>
          {s.lead}
        </p>

        {/* Block cards — auto-balance based on count (2/3/4 columns) */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${
            s.blocks.length === 3 ? "lg:grid-cols-3" : s.blocks.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-2"
          } gap-5 mt-12`}
        >
          {s.blocks.map((b, i) => (
            <div
              key={i}
              className={`p-7 ${dark ? "bg-white/5 border border-white/10" : "bg-[#fafafa] border border-[#e5e5e5]"}`}
            >
              <span className={`font-mono text-[10px] tracking-widest ${dark ? "text-white/50" : "text-[#525252]"}`}>{`B.0${i + 1}`}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-tight mt-3 leading-tight">{b.h}</h3>
              <p className={`text-sm mt-3 leading-relaxed ${dark ? "text-white/75" : "text-[#525252]"}`}>{b.t}</p>
            </div>
          ))}
        </div>

        {s.callout && (
          <p className={`mt-8 max-w-4xl text-base leading-relaxed border-l-2 pl-5 italic ${dark ? "text-white/85 border-[#5B82FF]" : "text-[#0a0a0a] border-[#0A4DFF]"}`}>
            {s.callout}
          </p>
        )}

        {s.cta && (
          <Link to={s.cta.to} className="mt-8 inline-flex items-center gap-2 gm-btn gm-btn-outline" data-testid={`rc-service-cta-${s.anchor}`}>
            {s.cta.label} <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </section>
  );
};

const FinalCta = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#0A4DFF] text-white" data-testid="rc-final-cta">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <span className="font-mono uppercase tracking-[0.22em] text-[12px] text-white/80">{c.eyebrow}</span>
      <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium max-w-4xl">{c.title}</h2>
      <p className="text-white/90 mt-5 leading-relaxed max-w-2xl text-base md:text-lg">{c.sub}</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <a href={`mailto:${c.email}`} className="gm-btn bg-white text-[#0A4DFF] border-white hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a]" data-testid="rc-cta-email">
          {c.email} <ArrowRight size={16} />
        </a>
        <Link to="/kontakt" className="text-sm font-mono tracking-widest uppercase text-white border-b border-white/30 pb-1 hover:border-white" data-testid="rc-cta-kontakt">
          → Kontaktní formulář
        </Link>
      </div>
    </div>
  </section>
);

export default function RetailConsulting() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.cs;
  return (
    <div data-testid="retail-consulting-page">
      <Seo
        title={{ cs: "Retail Consulting", en: "Retail Consulting" }}
        description={{
          cs:
            "Go-to-Market, vyjednávání s řetězci, JBP, lidské zdroje a instore data. 28+ let retailové praxe v CZ a SK.",
          en:
            "Go-to-Market, chain negotiation, JBP, sales org and instore data. 28+ years of retail practice in CZ and SK.",
        }}
        path="/retail-consulting"
      />
      <Hero c={c} />
      <Overview c={c.overview} services={c.services} />
      {c.services.map((s, i) => (
        <ServiceSection key={s.n} s={s} idx={i} />
      ))}
      <FinalCta c={c.finalCta} />
    </div>
  );
}
